/**
 * Note:
 * This file should be synced with the corresponding layout in Contenful CMS.
 */
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { ComponentSelector, Slot } from '@thd-olt-component-react/component-selector';
import {
  params,
  arrayOf,
  bool,
  string,
  fragment,
  shape,
  extend,
  customType,
  number,
} from '@thd-nucleus/data-sources';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { Hero } from '@thd-olt-component-react/hero';
import { Endcap } from '@thd-olt-component-react/endcap';
import { MedioInline, PiccolaPencil } from '@thd-olt-component-react/internal-marketing-banner';
import { BrowseSearchMetadata } from '@thd-olt-component-react/metadata';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { Resource } from '@thd-olt-component-react/resource';
import { ContentGalleryCard } from '@thd-olt-component-react/content-gallery-card';
import { SpecialBuyBanner } from '@thd-olt-component-react/special-buy-banner';
import { SideNavigation } from '@thd-olt-component-react/side-navigation';
import { Spotlight } from '@thd-olt-component-react/spotlight';
import { PromoVisualNavigation, VisualNavigation } from '@thd-olt-component-react/visual-navigation';
import { ProductShelf } from '@thd-olt-component-react/product-shelf';
import { LandingPageRedirector, LandingPageSearchRedirector } from '@thd-nucleus/app-render';
import { EducationalLayout } from '../templates/EducationalLayout';
import { NavigationalLayout } from '../templates/NavigationalLayout';
import { InspirationalLayout } from '../templates/InspirationalLayout';

const breadcrumbFragment = {
  breadcrumb: string(),
  breadcrumbs: shape({
    breadcrumbItem: arrayOf(shape({
      label: string(),
      url: string()
    }))
  })
};

const heroFragment = {
  heroCarousel: shape({
    id: string(),
    isCarousel: bool(),
    isAutoplay: bool(),
    components: arrayOf(
      shape({ ...Hero.dataModel.component, ...ComponentSelector.dataModel.component })
    ),
  }),
};

Slot.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['Slot'])
      .isRequired(),
  }).shape({
    Slot: fragment().shape({
      componentName: string(),
      id: string(),
      name: string(),
      slotNumber: number(),
      content: shape({
        ...Hero.dataModel.component,
        ...Spotlight.dataModel.component,
        ...Resource.dataModel.component,
      })
    })
  })
};

ComponentSelector.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['ComponentSelector']).isRequired(),
  }).shape({
    ComponentSelector: fragment().shape({
      componentName: string(),
      id: string(),
      targetingType: shape({
        AdobeTarget: fragment().shape({
          id: string(),
          mbox: string()
        }),
        MultiArmBandit: fragment().shape({
          id: string(),
          campaignId: string()
        })
      }),
      defaultComponent: arrayOf(
        shape({
          ...Hero.dataModel.component,
          ...Slot.dataModel.component
        })
      ),
      variations: arrayOf(
        shape({
          ...Hero.dataModel.component,
          ...Slot.dataModel.component
        })
      ),
    }),
  }),
};

export const dataModel = extend(
  {
    layouts: params({
      slug: string().isRequired(),
      customerType: string()
    }).shape({
      slug: string(),
      title: string(),
      type: string(),
      seo: shape({
        canonicalURL: string(),
        description: string(),
        facebookAdmins: string(),
        siteName: string(),
        title: string(),
        type: string(),
        linkData: string(),
        scriptData: string(),
        no_index: string(),
        no_follow: string()
      }),
      content: shape({
        __typename: string(),
        EducationalLayout: fragment().shape({
          ...breadcrumbFragment,
          ...heroFragment
        }),
        NavigationalLayout: fragment().shape({
          ...breadcrumbFragment,
          ...heroFragment
        }),
        InspirationalLayout: fragment().shape({
          ...breadcrumbFragment,
          ...heroFragment
        })
      }),
    }),
  },
  // you need this here too because the query is asked for at the root level
  EducationalLayout,
  NavigationalLayout,
  InspirationalLayout,
  Hero,
  ComponentSelector,
  ContentGalleryCard,
  SpecialBuyBanner,
  SideNavigation,
  VisualNavigation,
  PromoVisualNavigation,
  Breadcrumbs,
  BrowseSearchMetadata,
  RelatedSearch,
  Resource,
  Spotlight,
  EmtLinks,
  Endcap,
  LandingPageRedirector,
  LandingPageSearchRedirector,
  ProductShelf,
  Slot,
  MedioInline,
  PiccolaPencil
);